import { useQuery } from "@tanstack/react-query";
import type {
  ExpiryLotsParams,
  InventoryParams,
  OperationsParams,
  OrderThroughputParams,
  SalesByDayParams,
  SalesSummaryParams,
  WalletParams,
  WalletTransactionsParams,
  TransferReportParams,
  TransferReportAgingParams,
  TransferReportLinesParams,
  PurchaseOrderReportParams,
  LowStockReportParams,
} from "./client";
import { reportsApi } from "./client";

// Serialize Date objects to ISO strings for stable query keys
function serializeParams(params?: SalesSummaryParams | SalesByDayParams) {
  if (!params) return undefined;

  return {
    ...params,
    fromDate: params.fromDate?.toISOString(),
    toDate: params.toDate?.toISOString(),
    branchIds: params.branchIds,
    channel: params.channel,
    statusFilter: params.statusFilter,
    ...(params.groupBy && { groupBy: params.groupBy }),
  };
}

export const reportsKeys = {
  all: ["reports"] as const,
  sales: {
    all: ["reports", "sales"] as const,
    summary: (params?: SalesSummaryParams) =>
      [...reportsKeys.sales.all, "summary", serializeParams(params)] as const,
    byDay: (params?: SalesByDayParams) =>
      [...reportsKeys.sales.all, "by-day", serializeParams(params)] as const,
    byBranch: (params?: SalesSummaryParams) =>
      [...reportsKeys.sales.all, "by-branch", serializeParams(params)] as const,
  },
};

export function useSalesSummaryQuery(params?: SalesSummaryParams) {
  return useQuery({
    queryKey: reportsKeys.sales.summary(params),
    queryFn: () => reportsApi.getSalesSummary(params),
  });
}

export function useSalesByDayQuery(params?: SalesByDayParams) {
  return useQuery({
    queryKey: reportsKeys.sales.byDay(params),
    queryFn: () => reportsApi.getSalesByDay(params),
  });
}

export function useSalesByBranchQuery(params?: SalesSummaryParams) {
  return useQuery({
    queryKey: reportsKeys.sales.byBranch(params),
    queryFn: () => reportsApi.getSalesByBranch(params),
  });
}

export const operationsKeys = {
  all: ["reports", "operations"] as const,
  avgPrepTime: (params?: OperationsParams) =>
    [
      ...operationsKeys.all,
      "avg-prep-time",
      serializeOperationsParams(params),
    ] as const,
  throughput: (params?: OrderThroughputParams) =>
    [
      ...operationsKeys.all,
      "throughput",
      serializeOperationsParams(params),
    ] as const,
};

function serializeOperationsParams(
  params?: OperationsParams | OrderThroughputParams,
) {
  if (!params) return undefined;

  return {
    ...params,
    fromDate: params.fromDate?.toISOString(),
    toDate: params.toDate?.toISOString(),
    branchIds: params.branchIds,
    channel: params.channel,
    ...("groupBy" in params && params.groupBy && { groupBy: params.groupBy }),
  };
}

export function useAvgPrepTimeQuery(params?: OperationsParams) {
  return useQuery({
    queryKey: operationsKeys.avgPrepTime(params),
    queryFn: () => reportsApi.getAvgPrepTime(params),
  });
}

export function useOrderThroughputQuery(params?: OrderThroughputParams) {
  return useQuery({
    queryKey: operationsKeys.throughput(params),
    queryFn: () => reportsApi.getOrderThroughput(params),
  });
}

export const walletKeys = {
  all: ["reports", "wallet"] as const,
  summary: (params?: WalletParams) =>
    [...walletKeys.all, "summary", serializeWalletParams(params)] as const,
  transactions: (params?: WalletTransactionsParams) =>
    [...walletKeys.all, "transactions", serializeWalletParams(params)] as const,
  approvalsSummary: (params?: {
    fromDate?: Date;
    toDate?: Date;
    status?: "PENDING" | "APPROVED" | "REJECTED";
  }) =>
    [
      ...walletKeys.all,
      "approvals-summary",
      serializeWalletParams(params),
    ] as const,
};

function serializeWalletParams(
  params?:
    | WalletParams
    | WalletTransactionsParams
    | {
        fromDate?: Date;
        toDate?: Date;
        status?: "PENDING" | "APPROVED" | "REJECTED";
      },
) {
  if (!params) return undefined;

  return {
    ...params,
    fromDate: params.fromDate?.toISOString(),
    toDate: params.toDate?.toISOString(),
    transactionTypes: params.transactionTypes,
    customerId: "customerId" in params ? params.customerId : undefined,
    status: params.status,
    ...("limit" in params && params.limit && { limit: params.limit }),
    ...("offset" in params && params.offset && { offset: params.offset }),
  };
}

export function useWalletSummaryQuery(params?: WalletParams) {
  return useQuery({
    queryKey: walletKeys.summary(params),
    queryFn: () => reportsApi.getWalletSummary(params),
  });
}

export function useWalletTransactionsQuery(params?: WalletTransactionsParams) {
  return useQuery({
    queryKey: walletKeys.transactions(params),
    queryFn: () => reportsApi.getWalletTransactions(params),
  });
}

export function useWalletApprovalsSummaryQuery(params?: {
  fromDate?: Date;
  toDate?: Date;
  status?: "PENDING" | "APPROVED" | "REJECTED";
}) {
  return useQuery({
    queryKey: walletKeys.approvalsSummary(params),
    queryFn: () => reportsApi.getWalletApprovalsSummary(params),
  });
}

export const inventoryKeys = {
  all: ["reports", "inventory"] as const,
  summary: (params?: InventoryParams) =>
    [
      ...inventoryKeys.all,
      "summary",
      serializeInventoryParams(params),
    ] as const,
  expiryLots: (params?: ExpiryLotsParams) =>
    [
      ...inventoryKeys.all,
      "expiry-lots",
      serializeInventoryParams(params),
    ] as const,
  wastage: (params?: InventoryParams) =>
    [
      ...inventoryKeys.all,
      "wastage",
      serializeInventoryParams(params),
    ] as const,
};

function serializeInventoryParams(params?: InventoryParams | ExpiryLotsParams) {
  if (!params) return undefined;
  return {
    ...params,
    branchIds: params.branchIds,
    itemType: params.itemType,
    itemId: params.itemId,
    daysBeforeExpiry: params.daysBeforeExpiry,
    groupBy: params.groupBy,
    ...("limit" in params && params.limit && { limit: params.limit }),
    ...("offset" in params && params.offset && { offset: params.offset }),
  };
}

export function useInventorySummaryQuery(params?: InventoryParams) {
  return useQuery({
    queryKey: inventoryKeys.summary(params),
    queryFn: () => reportsApi.getInventorySummary(params),
  });
}

export function useExpiryLotsQuery(params?: ExpiryLotsParams) {
  return useQuery({
    queryKey: inventoryKeys.expiryLots(params),
    queryFn: () => reportsApi.getExpiryLots(params),
  });
}

export function useWastageReportQuery(params?: InventoryParams) {
  return useQuery({
    queryKey: inventoryKeys.wastage(params),
    queryFn: () => reportsApi.getWastageReport(params),
  });
}

function serializeTransferReportParams(params?: TransferReportParams) {
  if (!params) return undefined;
  return {
    ...params,
    fromDate: params.fromDate?.toISOString(),
    toDate: params.toDate?.toISOString(),
    dateField: params.dateField,
    fromBranchIds: params.fromBranchIds,
    statuses: params.statuses,
    groupBy: params.groupBy,
    ingredientCategoryId: params.ingredientCategoryId,
  };
}

export const transferReportKeys = {
  all: ["reports", "inventory", "transfers"] as const,
  summary: (params?: TransferReportParams) =>
    [
      ...transferReportKeys.all,
      "summary",
      serializeTransferReportParams(params),
    ] as const,
  byBranch: (params?: TransferReportParams) =>
    [
      ...transferReportKeys.all,
      "by-branch",
      serializeTransferReportParams(params),
    ] as const,
  byItem: (params?: TransferReportParams) =>
    [
      ...transferReportKeys.all,
      "by-item",
      serializeTransferReportParams(params),
    ] as const,
  byCategory: (params?: TransferReportParams) =>
    [
      ...transferReportKeys.all,
      "by-category",
      serializeTransferReportParams(params),
    ] as const,
  trend: (params?: TransferReportParams) =>
    [
      ...transferReportKeys.all,
      "trend",
      serializeTransferReportParams(params),
    ] as const,
  aging: (params?: TransferReportAgingParams) =>
    [...transferReportKeys.all, "aging", params] as const,
  lines: (params?: TransferReportLinesParams) =>
    [
      ...transferReportKeys.all,
      "lines",
      {
        ...serializeTransferReportParams(params),
        limit: params?.limit,
        offset: params?.offset,
      },
    ] as const,
};

export function useTransferReportSummaryQuery(params?: TransferReportParams) {
  return useQuery({
    queryKey: transferReportKeys.summary(params),
    queryFn: () => reportsApi.getTransferReportSummary(params),
  });
}

export function useTransferReportByBranchQuery(params?: TransferReportParams) {
  return useQuery({
    queryKey: transferReportKeys.byBranch(params),
    queryFn: () => reportsApi.getTransferReportByBranch(params),
  });
}

export function useTransferReportByItemQuery(params?: TransferReportParams) {
  return useQuery({
    queryKey: transferReportKeys.byItem(params),
    queryFn: () => reportsApi.getTransferReportByItem(params),
  });
}

export function useTransferReportByCategoryQuery(params?: TransferReportParams) {
  return useQuery({
    queryKey: transferReportKeys.byCategory(params),
    queryFn: () => reportsApi.getTransferReportByCategory(params),
  });
}

export function useTransferReportTrendQuery(params?: TransferReportParams) {
  return useQuery({
    queryKey: transferReportKeys.trend(params),
    queryFn: () => reportsApi.getTransferReportTrend(params),
  });
}

export function useTransferReportAgingQuery(params?: TransferReportAgingParams) {
  return useQuery({
    queryKey: transferReportKeys.aging(params),
    queryFn: () => reportsApi.getTransferReportAging(params),
  });
}

export function useTransferReportLinesQuery(params?: TransferReportLinesParams) {
  return useQuery({
    queryKey: transferReportKeys.lines(params),
    queryFn: () => reportsApi.getTransferReportLines(params),
  });
}

export const purchaseOrderReportKeys = {
  all: ["reports", "inventory", "purchase-orders"] as const,
  summary: (params?: PurchaseOrderReportParams) =>
    [...purchaseOrderReportKeys.all, "summary", params] as const,
  bySupplier: (params?: PurchaseOrderReportParams) =>
    [...purchaseOrderReportKeys.all, "by-supplier", params] as const,
  leadTime: (params?: PurchaseOrderReportParams) =>
    [...purchaseOrderReportKeys.all, "lead-time", params] as const,
};

export function usePurchaseOrderLeadTimeReportQuery(
  params?: PurchaseOrderReportParams,
) {
  return useQuery({
    queryKey: purchaseOrderReportKeys.leadTime(params),
    queryFn: () => reportsApi.getPurchaseOrderLeadTimeReport(params),
  });
}

export function usePurchaseOrderReportSummaryQuery(
  params?: PurchaseOrderReportParams,
) {
  return useQuery({
    queryKey: purchaseOrderReportKeys.summary(params),
    queryFn: () => reportsApi.getPurchaseOrderReportSummary(params),
  });
}

export function usePurchaseOrderReportBySupplierQuery(
  params?: PurchaseOrderReportParams,
) {
  return useQuery({
    queryKey: purchaseOrderReportKeys.bySupplier(params),
    queryFn: () => reportsApi.getPurchaseOrderReportBySupplier(params),
  });
}

export const lowStockReportKeys = {
  all: ["reports", "inventory", "low-stock"] as const,
  list: (params?: LowStockReportParams) =>
    [...lowStockReportKeys.all, params] as const,
};

export function useLowStockReportQuery(params?: LowStockReportParams) {
  return useQuery({
    queryKey: lowStockReportKeys.list(params),
    queryFn: () => reportsApi.getLowStockReport(params!),
    enabled: !!params?.branchId,
  });
}
